<template>
  <transition name="detail-fade">
    <div v-if="open" class="detail-mask" @click.self="close">
      <transition name="detail-slide">
        <aside v-if="open" class="detail-panel" role="dialog" aria-modal="true">
          <!-- 抽屉科技背景 -->
          <div class="drawer-grid-bg"></div>
          <div class="drawer-scan-beam"></div>
          <div class="drawer-aurora drawer-aurora-cyan"></div>
          <div class="drawer-aurora drawer-aurora-violet"></div>
          <div class="drawer-particles" aria-hidden="true">
            <i></i>
            <i></i>
            <i></i>
            <i></i>
          </div>
          <!-- 固定头部 -->
          <header class="detail-header">
            <div class="header-left">
              <button type="button" class="back-btn" @click="close">‹</button>
              <div>
                <span class="header-eyebrow">STRATEGY POSITION</span>
                <h2>策略持仓详情</h2>
              </div>
            </div>
            <button type="button" class="close-btn" @click="close">×</button>
          </header>

          <div v-if="order" class="detail-body">
            <!-- 订单主视觉 -->
            <section :class="['order-hero', isTrial ? 'trial' : 'standard']">
              <div class="hero-grid"></div>
              <div class="hero-glow"></div>

              <div class="hero-top">
                <div class="type-group">
                  <span :class="['type-badge', isTrial ? 'trial' : 'standard']">
                    {{ orderTypeText }}
                  </span>
                  <span class="rule-chip">{{ isTrial ? '7 DAY STRATEGY' : 'BTC MULTI-VENUE EXECUTION' }}</span>
                </div>

                <span :class="['status-badge', statusClass]">
                  <i></i>
                  {{ statusText }}
                </span>
              </div>

              <div class="hero-main">
                <span>策略编号</span>
                <h3>{{ order.orderNo || '--' }}</h3>
                <p>配置时间 {{ order.createTime || '--' }}</p>
              </div>

              <div v-if="!isTrial" class="hero-assets">
                <div>
                  <span>执行中额度</span>
                  <strong>{{ money(order.earningPrincipalUsdt) }}</strong>
                  <em>USDT</em>
                </div>
                <div>
                  <span>累计结算结果</span>
                  <strong class="profit">+{{ money(order.profitStaticUsdt) }}</strong>
                  <em>USDT</em>
                </div>
              </div>

              <div v-else class="hero-assets">
                <div>
                  <span>到期结算总额</span>
                  <strong>{{ money(trialExpectedTotal) }}</strong>
                  <em>USDT</em>
                </div>
                <div>
                  <span>预设结算结果</span>
                  <strong class="profit">+{{ money(order.trialProfitUsdt) }}</strong>
                  <em>USDT</em>
                </div>
              </div>
            </section>

            <!-- 标准执行策略 -->
            <template v-if="!isTrial">
              <section class="detail-section">
                <div class="section-title">
                  <div>
                    <span>ALLOCATION STRUCTURE</span>
                    <h3>策略额度结构</h3>
                  </div>
                  <b>STANDARD</b>
                </div>

                <div class="capital-total">
                  <div>
                    <span>初始配置额度</span>
                    <strong>{{ money(order.depositAmountUsdt) }} USDT</strong>
                  </div>
                  <small>100%</small>
                </div>

                <div class="capital-track">
                  <div class="fee-part" :style="{ width: feePercent + '%' }">
                    <span>{{ feePercent }}%</span>
                  </div>
                  <div class="pool-part" :style="{ width: poolPercent + '%' }">
                    <span>{{ poolPercent }}%</span>
                  </div>
                </div>

                <div class="capital-legend">
                  <div>
                    <i class="fee"></i>
                    <span>AI 策略服务费</span>
                    <strong>{{ money(modelFeeUsdt) }} U</strong>
                  </div>
                  <div>
                    <i class="pool"></i>
                    <span>初始执行额度</span>
                    <strong>{{ money(arbitragePrincipalUsdt) }} U</strong>
                  </div>
                </div>

                <div class="important-note">
                  <span>!</span>
                  <p>收益仅按照执行中额度结算。进入释放处理中的本金，从提交赎回申请时起立即停止产生收益。</p>
                </div>
              </section>

              <section class="detail-section">
                <div class="section-title">
                  <div>
                    <span>ALLOCATION STATUS</span>
                    <h3>执行额度状态</h3>
                  </div>
                  <b>{{ redeemProgress }}%</b>
                </div>

                <div class="principal-grid">
                  <div class="principal-card earning">
                    <span>执行中额度</span>
                    <strong>{{ money(order.earningPrincipalUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                  <div class="principal-card releasing">
                    <span>释放处理中</span>
                    <strong>{{ money(order.releasingPrincipalUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                  <div class="principal-card redeemed">
                    <span>已释放额度</span>
                    <strong>{{ money(order.redeemedPrincipalUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                </div>

                <div class="redeem-progress">
                  <div class="progress-head">
                    <span>额度释放总进度</span>
                    <b>{{ redeemCompletedTimes }} / {{ redeemTotalTimes }} 次</b>
                  </div>
                  <div class="progress-track">
                    <div :style="{ width: redeemProgress + '%' }"></div>
                  </div>
                  <div class="progress-foot">
                    <span>单次释放 {{ redeemUnitPercent }}%</span>
                    <span>剩余可提交 {{ remainingRedeemTimes }} 次</span>
                  </div>
                </div>
              </section>

              <section class="detail-section">
                <div class="section-title">
                  <div>
                    <span>STRATEGY SETTLEMENT</span>
                    <h3>策略结算</h3>
                  </div>
                  <b>{{ rateSourceText }}</b>
                </div>

                <div class="data-list">
                  <div>
                    <span>当前日结算参数</span>
                    <strong>{{ percent(appliedDailyRate) }}</strong>
                  </div>
                  <div>
                    <span>累计执行天数</span>
                    <strong>{{ number(order.settledDays) }} 天</strong>
                  </div>
                  <div>
                    <span>累计结算结果</span>
                    <strong class="positive">+{{ money(order.profitStaticUsdt) }} USDT</strong>
                  </div>
                  <div>
                    <span>可提取结算余额</span>
                    <strong>{{ money(availableProfitUsdt) }} USDT</strong>
                  </div>
                  <div>
                    <span>资产提取处理中</span>
                    <strong>{{ money(order.withdrawingProfitUsdt) }} USDT</strong>
                  </div>
                  <div>
                    <span>累计已提取结算</span>
                    <strong>{{ money(order.withdrawnProfitUsdt) }} USDT</strong>
                  </div>
                  <div>
                    <span>策略启用日期</span>
                    <strong>{{ order.startDate || '--' }}</strong>
                  </div>
                  <div>
                    <span>最后结算日期</span>
                    <strong>{{ order.lastSettleDate || '--' }}</strong>
                  </div>
                </div>
              </section>

              <section class="detail-section">
                <div class="section-title">
                  <div>
                    <span>STRATEGY RULES</span>
                    <h3>策略规则</h3>
                  </div>
                </div>

                <div class="rule-list">
                  <div>
                    <i>01</i>
                    <p><b>有效执行额度</b><span>仅当前有效计息本金参与每日策略结算。</span></p>
                  </div>
                  <div>
                    <i>02</i>
                    <p><b>额度释放</b><span>每次按照初始执行额度的 {{ redeemUnitPercent }}% 发起线性释放。</span></p>
                  </div>
                  <div>
                    <i>03</i>
                    <p><b>释放期间</b><span>释放处理中的额度不再参与策略执行，完成后计入已释放额度。</span></p>
                  </div>
                  <div>
                    <i>04</i>
                    <p><b>结算参数</b><span>策略专属参数优先，其次采用账户专属参数，最后使用系统基准参数。</span></p>
                  </div>
                </div>
              </section>
            </template>

            <!-- 七天体验策略 -->
            <template v-else>
              <section class="detail-section trial-section">
                <div class="section-title">
                  <div>
                    <span>EXPERIENCE STRATEGY</span>
                    <h3>体验策略</h3>
                  </div>
                  <b>U IN · U OUT</b>
                </div>

                <div class="trial-summary-grid">
                  <div>
                    <span>体验配置额度</span>
                    <strong>{{ money(order.depositAmountUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                  <div>
                    <span>预设结算结果</span>
                    <strong class="positive">+{{ money(order.trialProfitUsdt) }}</strong>
                    <em>USDT</em>
                  </div>
                  <div>
                    <span>策略周期</span>
                    <strong>{{ number(order.trialDays || 7) }}</strong>
                    <em>DAYS</em>
                  </div>
                  <div>
                    <span>到期结算总额</span>
                    <strong>{{ money(trialExpectedTotal) }}</strong>
                    <em>USDT</em>
                  </div>
                </div>

                <div class="cycle-panel">
                  <div class="cycle-head">
                    <div>
                      <span>体验策略进度</span>
                      <b>{{ number(order.settledDays) }} / {{ number(order.trialDays || 7) }} 天</b>
                    </div>
                    <strong>{{ cycleProgress }}%</strong>
                  </div>
                  <div class="progress-track trial">
                    <div :style="{ width: cycleProgress + '%' }"></div>
                  </div>
                  <div class="cycle-dates">
                    <span><i></i>开始：{{ order.startDate || '--' }}</span>
                    <span><i></i>到期：{{ order.maturityDate || '--' }}</span>
                  </div>
                </div>

                <div :class="['maturity-card', { ready: canTrialWithdraw }]">
                  <div class="maturity-icon">{{ canTrialWithdraw ? '✓' : '7D' }}</div>
                  <div>
                    <span>{{ canTrialWithdraw ? '策略周期已完成' : '体验策略运行中' }}</span>
                    <strong>
                      {{ canTrialWithdraw ? '当前可提交到期统一结算' : '周期完成后统一结算 ' + money(order.depositAmountUsdt) + ' USDT 本金及预设结算结果' }}
                    </strong>
                  </div>
                </div>
              </section>

              <section class="detail-section">
                <div class="section-title">
                  <div>
                    <span>STRATEGY PARAMETERS</span>
                    <h3>体验策略规则</h3>
                  </div>
                </div>

                <div class="eligibility-list">
                  <div><i>✓</i><span>每个钱包地址仅可配置一次体验策略</span></div>
                  <div><i>✓</i><span>策略周期固定为七天，不支持提前赎回</span></div>
                  <div><i>✓</i><span>使用 USDT 配置，并以 USDT 完成到期结算</span></div>
                  <div><i>×</i><span>不纳入合作网络配置规模及协作激励</span></div>
                </div>
              </section>
            </template>

            <!-- 链上信息 -->
            <section class="detail-section">
              <div class="section-title">
                <div>
                  <span>ON-CHAIN CREDENTIAL</span>
                  <h3>链上凭证</h3>
                </div>
                <b>BSC</b>
              </div>

              <div class="chain-list">
                <div>
                  <span>配置资产</span>
                  <strong>{{ order.depositToken || 'USDT' }}</strong>
                </div>
                <div>
                  <span>部署网络</span>
                  <strong>{{ order.chainCode || 'BSC' }}</strong>
                </div>
                <div class="full">
                  <span>链上交易哈希</span>
                  <strong class="mono">{{ shortHash(order.depositTxHash) }}</strong>
                </div>
                <div class="full">
                  <span>钱包地址</span>
                  <strong class="mono">{{ shortHash(order.userCode || walletAddress) }}</strong>
                </div>
              </div>
            </section>
          </div>

          <!-- 固定底部操作 -->
          <footer v-if="order" class="detail-footer">
            <button type="button" class="secondary-btn" @click="close">返回策略持仓</button>

            <button
              v-if="!isTrial"
              type="button"
              class="primary-action"
              :disabled="!canRedeem"
              @click="applyRedeem"
            >
              {{ canRedeem ? '申请额度释放' : '暂不可释放' }}
            </button>

            <button
              v-else
              type="button"
              :class="['primary-action', 'trial', { ready: canTrialWithdraw }]"
              :disabled="!canTrialWithdraw"
              @click="claimTrial"
            >
              {{ canTrialWithdraw ? '提交到期结算' : '周期完成后可结算' }}
            </button>
          </footer>
        </aside>
      </transition>
    </div>
  </transition>
</template>

<script>
export default {
name: 'BchOrderDetailDrawer',
props: {
  open: {
    type: Boolean,
    default: false
  },
  order: {
    type: Object,
    default: null
  },
  walletAddress: {
    type: String,
    default: ''
  }
},
computed: {
  isTrial() {
    return Number(this.order && this.order.orderType) === 2
  },
  orderTypeText() {
    return this.isTrial ? '七天体验策略' : '标准执行策略'
  },
  statusText() {
    const item = this.order || {}

    if (!this.isTrial && Number(item.redeemStatus) === 1) {
      return '部分额度释放中'
    }
    if (!this.isTrial && Number(item.redeemStatus) === 2) {
      return '全部额度释放中'
    }

    const map = {
      0: '待网络确认',
      1: '策略执行中',
      2: this.isTrial ? '待到期结算' : '执行已停止',
      3: '策略已结算',
      4: '策略已取消',
      5: '策略处理失败'
    }
    return map[Number(item.status)] || '状态待确认'
  },
  statusClass() {
    const item = this.order || {}

    if (!this.isTrial && [1, 2].indexOf(Number(item.redeemStatus)) !== -1) {
      return 'redeeming'
    }

    const map = {
      0: 'pending',
      1: 'running',
      2: this.isTrial ? 'matured' : 'stopped',
      3: 'finished',
      4: 'cancelled',
      5: 'failed'
    }
    return map[Number(item.status)] || 'pending'
  },
  modelFeeUsdt() {
    const item = this.order || {}
    if (item.modelFeeUsdt !== undefined && item.modelFeeUsdt !== null) {
      return Number(item.modelFeeUsdt || 0)
    }
    return Number(item.depositAmountUsdt || 0) * 0.3
  },
  arbitragePrincipalUsdt() {
    const item = this.order || {}
    if (item.arbitragePrincipalUsdt !== undefined && item.arbitragePrincipalUsdt !== null) {
      return Number(item.arbitragePrincipalUsdt || 0)
    }
    return Math.max(Number(item.depositAmountUsdt || 0) - this.modelFeeUsdt, 0)
  },
  feePercent() {
    const total = Number((this.order && this.order.depositAmountUsdt) || 0)
    if (total <= 0) return 30
    return Math.round(this.modelFeeUsdt / total * 10000) / 100
  },
  poolPercent() {
    return Math.max(100 - this.feePercent, 0)
  },
  redeemUnitPercent() {
    const raw = this.order && this.order.redeemUnitRate
    if (raw === undefined || raw === null || raw === '') return 20
    const value = Number(raw)
    return value <= 1 ? Number((value * 100).toFixed(2)) : value
  },
  redeemTotalTimes() {
    const value = Number((this.order && this.order.redeemTotalTimes) || 5)
    return value > 0 ? value : 5
  },
  redeemAppliedTimes() {
    return Math.max(Number((this.order && this.order.redeemAppliedTimes) || 0), 0)
  },
  redeemCompletedTimes() {
    const item = this.order || {}
    if (item.redeemCompletedTimes !== undefined && item.redeemCompletedTimes !== null) {
      return Math.max(Number(item.redeemCompletedTimes || 0), 0)
    }

    return Math.min(
      Math.floor(this.redeemProgress / Math.max(this.redeemUnitPercent, 1)),
      this.redeemTotalTimes
    )
  },
  remainingRedeemTimes() {
    return Math.max(this.redeemTotalTimes - this.redeemAppliedTimes, 0)
  },
  redeemProgress() {
    const item = this.order || {}

    if (item.redeemProgress !== undefined && item.redeemProgress !== null) {
      return this.clamp(item.redeemProgress)
    }

    const principal = this.arbitragePrincipalUsdt
    if (principal <= 0) return 0

    const redeemed = Number(item.redeemedPrincipalUsdt || 0)
    const releasing = Number(item.releasingPrincipalUsdt || 0)
    return this.clamp((redeemed + releasing) / principal * 100)
  },
  rateSourceText() {
    const item = this.order || {}
    const mode = Number(item.rateMode || 1)
    const map = {
      1: '系统基准参数',
      2: '账户专属参数',
      3: '策略专属参数',
      4: '固定结算金额',
      5: '固定结算参数'
    }
    return map[mode] || '系统基准参数'
  },
  appliedDailyRate() {
    const item = this.order || {}

    if (item.lastAppliedDailyRate !== undefined && item.lastAppliedDailyRate !== null) {
      return Number(item.lastAppliedDailyRate || 0)
    }
    if (item.customDailyRate !== undefined && item.customDailyRate !== null) {
      return Number(item.customDailyRate || 0)
    }
    if (item.dailyRate !== undefined && item.dailyRate !== null) {
      return Number(item.dailyRate || 0)
    }
    return 0
  },
  availableProfitUsdt() {
    const item = this.order || {}
    if (item.availableProfitUsdt !== undefined && item.availableProfitUsdt !== null) {
      return Number(item.availableProfitUsdt || 0)
    }
    return Number(item.profitStaticUsdt || 0)
  },
  trialExpectedTotal() {
    const item = this.order || {}
    if (item.expectedTotalUsdt !== undefined && item.expectedTotalUsdt !== null) {
      return Number(item.expectedTotalUsdt || 0)
    }
    return Number(item.depositAmountUsdt || 0) + Number(item.trialProfitUsdt || 0)
  },
  cycleProgress() {
    const item = this.order || {}

    if (item.cycleProgress !== undefined && item.cycleProgress !== null) {
      return this.clamp(item.cycleProgress)
    }

    const days = Number(item.trialDays || 7)
    if (days <= 0) return 100
    return this.clamp(Number(item.settledDays || 0) / days * 100)
  },
  trialAlreadyClaimed() {
    const item = this.order || {}
    return Number(item.status) === 3 ||
      Number(item.claimStatus) === 2 ||
      Number(item.trialClaimStatus) === 2
  },
  trialClaimProcessing() {
    const item = this.order || {}
    return Number(item.claimStatus) === 1 ||
      Number(item.trialClaimStatus) === 1
  },
  canTrialWithdraw() {
    const item = this.order || {}
    const matured = Boolean(item.canWithdraw) || Number(item.status) === 2
    return this.isTrial &&
      matured &&
      !this.trialAlreadyClaimed &&
      !this.trialClaimProcessing
  },
  canRedeem() {
    const item = this.order || {}

    return !this.isTrial &&
      Number(item.status) === 1 &&
      Number(item.earningPrincipalUsdt || 0) > 0 &&
      Number(item.redeemStatus || 0) !== 2 &&
      this.redeemAppliedTimes < this.redeemTotalTimes
  }
},
watch: {
  open: {
    immediate: true,
    handler(value) {
      if (typeof document === 'undefined') return
      document.body.style.overflow = value ? 'hidden' : ''
    }
  }
},
beforeDestroy() {
  if (typeof document !== 'undefined') {
    document.body.style.overflow = ''
  }
},
methods: {
  close() {
    this.$emit('close')
  },
  applyRedeem() {
    if (!this.canRedeem || !this.order) return
    this.$emit('redeem', { ...this.order })
  },
  claimTrial() {
    if (!this.canTrialWithdraw || !this.order) return
    this.$emit('claim-trial', { ...this.order })
  },
  clamp(value) {
    return Math.max(0, Math.min(Number(value || 0), 100))
  },
  number(value) {
    return Number(value || 0).toLocaleString('en-US', {
      maximumFractionDigits: 2
    })
  },
  money(value) {
    return Number(value || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    })
  },
  percent(value) {
    const numeric = Number(value || 0)
    const result = numeric <= 1 ? numeric * 100 : numeric
    return result.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 4
    }) + '%'
  },
  shortHash(value) {
    if (!value) return '--'
    const text = String(value)
    if (text.length <= 20) return text
    return text.slice(0, 10) + '...' + text.slice(-8)
  }
}
}
</script>

<style lang="less" scoped>
.detail-mask {
  position: fixed;
  z-index: 9999;
  inset: 0;
  display: flex;
  justify-content: flex-end;
  background: rgba(1, 4, 10, .76);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.detail-panel {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  color: #fff;
  background:
    radial-gradient(circle at 80% 0%, rgba(212, 175, 55, .13), transparent 24%),
    radial-gradient(circle at 0% 26%, rgba(37, 99, 235, .12), transparent 30%),
    linear-gradient(180deg, #070b13 0%, #09111e 58%, #060a12 100%);
  box-shadow: -30px 0 90px rgba(0, 0, 0, .58);
}

.detail-header {
  position: relative;
  z-index: 10;
  flex: 0 0 auto;
  min-height: 72px;
  padding: 12px 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, .08);
  background: rgba(6, 10, 18, .88);
  backdrop-filter: blur(16px);
}

.header-left {
  display: flex;
  align-items: center;
  gap: 11px;
}

.back-btn,
.close-btn {
  width: 42px;
  height: 42px;
  border-radius: 14px;
  border: 1px solid rgba(255, 255, 255, .1);
  color: #dbeafe;
  background: rgba(255, 255, 255, .055);
  font-size: 28px;
  cursor: pointer;
}

.close-btn {
  font-size: 22px;
}

.header-eyebrow {
  display: block;
  color: #7f94b8;
  font-size: 9px;
  font-weight: 900;
  letter-spacing: 1.6px;
}

.detail-header h2 {
  margin: 3px 0 0;
  font-size: 18px;
}

.detail-body {
  flex: 1;
  overflow-y: auto;
  padding: 12px 12px 120px;
  -webkit-overflow-scrolling: touch;
}

.order-hero {
  position: relative;
  overflow: hidden;
  min-height: 290px;
  padding: 18px 16px;
  border-radius: 27px;
  border: 1px solid rgba(212, 175, 55, .22);
  background:
    radial-gradient(circle at 85% 5%, rgba(212, 175, 55, .28), transparent 26%),
    radial-gradient(circle at 0% 100%, rgba(37, 99, 235, .22), transparent 34%),
    linear-gradient(145deg, rgba(20, 29, 48, .96), rgba(8, 14, 25, .98));
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .07), 0 22px 58px rgba(0, 0, 0, .36);
}

.order-hero.trial {
  border-color: rgba(59, 130, 246, .3);
  background:
    radial-gradient(circle at 85% 5%, rgba(59, 130, 246, .3), transparent 28%),
    radial-gradient(circle at 0% 100%, rgba(212, 175, 55, .15), transparent 34%),
    linear-gradient(145deg, rgba(16, 28, 52, .97), rgba(7, 14, 27, .98));
}

.hero-grid {
  position: absolute;
  inset: 0;
  opacity: .55;
  background-image:
    linear-gradient(rgba(255, 255, 255, .028) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, .028) 1px, transparent 1px);
  background-size: 28px 28px;
}

.hero-glow {
  position: absolute;
  right: -90px;
  top: -90px;
  width: 220px;
  height: 220px;
  border-radius: 50%;
  filter: blur(54px);
  background: rgba(212, 175, 55, .18);
}

.order-hero.trial .hero-glow {
  background: rgba(59, 130, 246, .2);
}

.hero-top,
.hero-main,
.hero-assets {
  position: relative;
  z-index: 2;
}

.hero-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.type-group {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}

.type-badge,
.rule-chip,
.status-badge {
  min-height: 26px;
  padding: 0 9px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  font-size: 9px;
  font-weight: 950;
  letter-spacing: .35px;
}

.type-badge.standard {
  color: #ffe08a;
  border: 1px solid rgba(212, 175, 55, .3);
  background: rgba(212, 175, 55, .12);
}

.type-badge.trial {
  color: #93c5fd;
  border: 1px solid rgba(59, 130, 246, .3);
  background: rgba(37, 99, 235, .14);
}

.rule-chip {
  color: #8398ba;
  background: rgba(255, 255, 255, .055);
}

.status-badge {
  flex-shrink: 0;
  gap: 6px;
  color: #9fb3d7;
  background: rgba(255, 255, 255, .055);
}

.status-badge i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
  box-shadow: 0 0 10px currentColor;
}

.status-badge.running { color: #4ade80; background: rgba(34, 197, 94, .11); }
.status-badge.redeeming { color: #ffe08a; background: rgba(212, 175, 55, .11); }
.status-badge.matured { color: #60a5fa; background: rgba(59, 130, 246, .12); }
.status-badge.finished { color: #a7f3d0; background: rgba(16, 185, 129, .11); }
.status-badge.pending { color: #cbd5e1; }
.status-badge.stopped { color: #fbbf24; background: rgba(245, 158, 11, .11); }
.status-badge.cancelled,
.status-badge.failed { color: #fb7185; background: rgba(244, 63, 94, .11); }

.hero-main {
  margin-top: 30px;
}

.hero-main > span {
  color: #8196ba;
  font-size: 11px;
}

.hero-main h3 {
  margin: 7px 0 0;
  font-size: 19px;
  line-height: 1.3;
  word-break: break-all;
}

.hero-main p {
  margin: 7px 0 0;
  color: #64799d;
  font-size: 11px;
}

.hero-assets {
  margin-top: 26px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.hero-assets > div {
  padding: 14px 12px;
  border-radius: 18px;
  background: rgba(255, 255, 255, .055);
  border: 1px solid rgba(255, 255, 255, .075);
}

.hero-assets span,
.principal-card span,
.trial-summary-grid span {
  display: block;
  color: #879bbd;
  font-size: 10px;
}

.hero-assets strong,
.principal-card strong,
.trial-summary-grid strong {
  display: block;
  margin-top: 7px;
  font-size: 21px;
}

.hero-assets strong.profit,
.positive {
  color: #4ade80 !important;
}

.hero-assets em,
.principal-card em,
.trial-summary-grid em {
  display: block;
  margin-top: 4px;
  color: #ffe08a;
  font-size: 9px;
  font-style: normal;
  letter-spacing: .4px;
}

.detail-section {
  margin-top: 12px;
  padding: 16px 14px;
  border-radius: 24px;
  background: linear-gradient(180deg, rgba(17, 27, 46, .86), rgba(8, 14, 25, .94));
  border: 1px solid rgba(148, 163, 184, .13);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .045), 0 16px 40px rgba(0, 0, 0, .2);
}

.section-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.section-title span {
  display: block;
  color: #607596;
  font-size: 8px;
  font-weight: 900;
  letter-spacing: 1.25px;
}

.section-title h3 {
  margin: 4px 0 0;
  font-size: 17px;
}

.section-title > b {
  padding: 7px 9px;
  border-radius: 10px;
  color: #ffe08a;
  background: rgba(212, 175, 55, .1);
  font-size: 9px;
  letter-spacing: .35px;
}

.capital-total {
  margin-top: 17px;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
}

.capital-total span {
  display: block;
  color: #8297b9;
  font-size: 10px;
}

.capital-total strong {
  display: block;
  margin-top: 6px;
  font-size: 20px;
}

.capital-total small {
  color: #ffe08a;
  font-weight: 900;
}

.capital-track,
.progress-track {
  margin-top: 13px;
  height: 11px;
  display: flex;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(255, 255, 255, .065);
}

.capital-track > div {
  position: relative;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: width .3s ease;
}

.capital-track span {
  color: #07101d;
  font-size: 8px;
  font-weight: 950;
}

.fee-part { background: linear-gradient(90deg, #8b6c1c, #d4af37); }
.pool-part { background: linear-gradient(90deg, #2563eb, #60a5fa); }

.capital-legend {
  margin-top: 13px;
  display: grid;
  gap: 9px;
}

.capital-legend > div {
  padding: 11px 12px;
  border-radius: 15px;
  display: grid;
  grid-template-columns: 8px 1fr auto;
  align-items: center;
  gap: 9px;
  background: rgba(255, 255, 255, .04);
}

.capital-legend i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.capital-legend i.fee { background: #d4af37; box-shadow: 0 0 10px rgba(212, 175, 55, .6); }
.capital-legend i.pool { background: #3b82f6; box-shadow: 0 0 10px rgba(59, 130, 246, .6); }
.capital-legend span { color: #8ea4c8; font-size: 11px; }
.capital-legend strong { font-size: 12px; }

.important-note {
  margin-top: 13px;
  padding: 12px;
  border-radius: 16px;
  display: flex;
  gap: 10px;
  color: #d9cc98;
  background: rgba(212, 175, 55, .085);
  border: 1px solid rgba(212, 175, 55, .18);
}

.important-note > span {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #090d14;
  background: #ffe08a;
  font-weight: 950;
}

.important-note p {
  margin: 0;
  font-size: 11px;
  line-height: 1.75;
}

.principal-grid,
.trial-summary-grid {
  margin-top: 15px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.principal-card,
.trial-summary-grid > div {
  min-width: 0;
  padding: 13px 11px;
  border-radius: 17px;
  background: rgba(255, 255, 255, .045);
  border: 1px solid rgba(255, 255, 255, .065);
}

.principal-card.earning {
  grid-column: 1 / -1;
  background: rgba(37, 99, 235, .11);
  border-color: rgba(59, 130, 246, .2);
}

.principal-card.earning strong { color: #93c5fd; }
.principal-card.releasing strong { color: #ffe08a; }
.principal-card.redeemed strong { color: #a7f3d0; }

.redeem-progress,
.cycle-panel {
  margin-top: 14px;
  padding: 13px 12px;
  border-radius: 17px;
  background: rgba(255, 255, 255, .035);
  border: 1px solid rgba(255, 255, 255, .055);
}

.progress-head,
.progress-foot,
.cycle-head,
.cycle-dates {
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

.progress-head span,
.progress-foot span,
.cycle-head span,
.cycle-dates span {
  color: #8196b7;
  font-size: 10px;
}

.progress-head b,
.cycle-head b,
.cycle-head > strong {
  color: #ffe08a;
  font-size: 11px;
}

.progress-track {
  height: 8px;
}

.progress-track > div {
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #d4af37, #ffe08a);
  box-shadow: 0 0 14px rgba(212, 175, 55, .28);
}

.progress-track.trial > div {
  background: linear-gradient(90deg, #2563eb, #60a5fa);
  box-shadow: 0 0 14px rgba(59, 130, 246, .28);
}

.progress-foot,
.cycle-dates {
  margin-top: 9px;
  flex-wrap: wrap;
}

.data-list,
.chain-list {
  margin-top: 13px;
  border-radius: 17px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, .055);
}

.data-list > div,
.chain-list > div {
  min-height: 48px;
  padding: 10px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: rgba(255, 255, 255, .026);
  border-bottom: 1px solid rgba(255, 255, 255, .05);
}

.data-list > div:last-child,
.chain-list > div:last-child {
  border-bottom: none;
}

.data-list span,
.chain-list span {
  color: #8196b8;
  font-size: 11px;
}

.data-list strong,
.chain-list strong {
  max-width: 62%;
  color: #eaf1ff;
  text-align: right;
  font-size: 11px;
}

.rule-list,
.eligibility-list {
  margin-top: 13px;
  display: grid;
  gap: 9px;
}

.rule-list > div {
  padding: 11px;
  border-radius: 16px;
  display: flex;
  gap: 10px;
  background: rgba(255, 255, 255, .035);
}

.rule-list i {
  flex: 0 0 29px;
  width: 29px;
  height: 29px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #ffe08a;
  background: rgba(212, 175, 55, .1);
  font-size: 9px;
  font-style: normal;
  font-weight: 950;
}

.rule-list p {
  margin: 0;
}

.rule-list b,
.rule-list span {
  display: block;
}

.rule-list b {
  font-size: 11px;
}

.rule-list span {
  margin-top: 4px;
  color: #8094b5;
  font-size: 10px;
  line-height: 1.55;
}

.trial-section {
  border-color: rgba(59, 130, 246, .16);
}

.trial-summary-grid > div:nth-child(4) {
  background: rgba(37, 99, 235, .11);
  border-color: rgba(59, 130, 246, .2);
}

.cycle-head {
  align-items: flex-end;
}

.cycle-head span,
.cycle-head b {
  display: block;
}

.cycle-head b {
  margin-top: 5px;
}

.cycle-head > strong {
  font-size: 17px;
  color: #93c5fd;
}

.cycle-dates span {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}

.cycle-dates i {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #60a5fa;
}

.maturity-card {
  margin-top: 13px;
  padding: 13px;
  border-radius: 17px;
  display: flex;
  align-items: center;
  gap: 11px;
  background: rgba(255, 255, 255, .035);
  border: 1px solid rgba(255, 255, 255, .06);
}

.maturity-card.ready {
  background: rgba(16, 185, 129, .08);
  border-color: rgba(52, 211, 153, .18);
}

.maturity-icon {
  flex: 0 0 42px;
  width: 42px;
  height: 42px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #93c5fd;
  background: rgba(37, 99, 235, .13);
  font-weight: 950;
}

.maturity-card.ready .maturity-icon {
  color: #a7f3d0;
  background: rgba(16, 185, 129, .13);
}

.maturity-card span,
.maturity-card strong {
  display: block;
}

.maturity-card span {
  color: #8196b7;
  font-size: 10px;
}

.maturity-card strong {
  margin-top: 5px;
  font-size: 11px;
  line-height: 1.55;
}

.eligibility-list > div {
  min-height: 43px;
  padding: 10px 11px;
  border-radius: 14px;
  display: flex;
  align-items: center;
  gap: 9px;
  color: #bdcbe1;
  background: rgba(255, 255, 255, .035);
  font-size: 10px;
}

.eligibility-list i {
  flex: 0 0 22px;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #06100c;
  background: #6ee7b7;
  font-style: normal;
  font-weight: 950;
}

.chain-list {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.chain-list > div {
  display: block;
  border-right: 1px solid rgba(255, 255, 255, .05);
}

.chain-list > div.full {
  grid-column: 1 / -1;
  border-right: none;
}

.chain-list span,
.chain-list strong {
  display: block;
  max-width: none;
  text-align: left;
}

.chain-list strong {
  margin-top: 6px;
  word-break: break-all;
}

.mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

.detail-footer {
  position: absolute;
  z-index: 12;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 12px calc(12px + env(safe-area-inset-bottom));
  display: grid;
  grid-template-columns: .9fr 1.1fr;
  gap: 10px;
  background: linear-gradient(180deg, rgba(6, 10, 18, .35), rgba(6, 10, 18, .98) 28%);
  backdrop-filter: blur(16px);
}

.detail-footer button {
  min-height: 50px;
  border-radius: 17px;
  font-size: 12px;
  font-weight: 950;
  cursor: pointer;
}

.secondary-btn {
  color: #cbd8ea;
  border: 1px solid rgba(255, 255, 255, .1);
  background: rgba(255, 255, 255, .055);
}

.primary-action {
  color: #080b12;
  border: none;
  background: linear-gradient(135deg, #d4af37, #ffe08a);
  box-shadow: 0 12px 30px rgba(212, 175, 55, .22);
}

.primary-action.trial {
  color: #dbeafe;
  background: linear-gradient(135deg, #1d4ed8, #3b82f6);
  box-shadow: 0 12px 30px rgba(37, 99, 235, .22);
}

.detail-footer button:disabled {
  cursor: not-allowed;
  color: #64748b;
  background: rgba(255, 255, 255, .065);
  box-shadow: none;
}

.detail-fade-enter-active,
.detail-fade-leave-active {
  transition: opacity .25s ease;
}

.detail-fade-enter,
.detail-fade-leave-to {
  opacity: 0;
}

.detail-slide-enter-active,
.detail-slide-leave-active {
  transition: transform .3s cubic-bezier(.22, .78, .24, 1);
}

.detail-slide-enter,
.detail-slide-leave-to {
  transform: translateX(100%);
}

@media (min-width: 768px) {
  .detail-panel {
   width: 92vw;
   max-width: 680px;
    border-left: 1px solid rgba(255, 255, 255, .08);
  }

  .detail-body {
    padding: 18px 18px 124px;
  }

  .order-hero {
    min-height: 330px;
    padding: 23px 22px;
  }

  .hero-main h3 {
    font-size: 23px;
  }

  .hero-assets > div {
    padding: 17px 15px;
  }

  .hero-assets strong {
    font-size: 27px;
  }

  .principal-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  .principal-card.earning {
    grid-column: auto;
  }

  .trial-summary-grid {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  .data-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .data-list > div:nth-child(odd) {
    border-right: 1px solid rgba(255, 255, 255, .05);
  }

  .detail-section {
    padding: 19px 18px;
  }
}


/* =========================================================
 * PROFESSIONAL STRATEGY DETAIL ENHANCEMENT
 * 深海蓝 + 冷紫 + 青色电光，和策略仓保持统一
 * ========================================================= */

.detail-panel {
  position: relative;
  isolation: isolate;
  border-left: 1px solid rgba(103, 232, 249, .13);
  background:
    radial-gradient(circle at 88% 2%, rgba(124, 58, 237, .18), transparent 27%),
    radial-gradient(circle at 0% 32%, rgba(6, 182, 212, .15), transparent 31%),
    linear-gradient(180deg, #050814 0%, #080d1b 54%, #050910 100%);
}

/* 背景网格 */
.drawer-grid-bg {
  position: absolute;
  z-index: -4;
  inset: 0;
  pointer-events: none;
  opacity: .48;
  background-image:
    linear-gradient(rgba(103, 232, 249, .028) 1px, transparent 1px),
    linear-gradient(90deg, rgba(167, 139, 250, .026) 1px, transparent 1px);
  background-size: 28px 28px;
  animation: detailGridMove 20s linear infinite;
}

/* 扫描光束 */
.drawer-scan-beam {
  position: absolute;
  z-index: -2;
  top: -140px;
  bottom: -140px;
  left: -150px;
  width: 130px;
  pointer-events: none;
  transform: skewX(-20deg);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(103, 232, 249, .045),
    rgba(255, 255, 255, .075),
    transparent
  );
  animation: detailBeamSweep 9s ease-in-out infinite;
}

/* 极光 */
.drawer-aurora {
  position: absolute;
  z-index: -3;
  width: 250px;
  height: 250px;
  border-radius: 50%;
  filter: blur(70px);
  pointer-events: none;
}

.drawer-aurora-cyan {
  left: -150px;
  top: 24%;
  background: rgba(6, 182, 212, .19);
  animation: detailAuroraFloat 9s ease-in-out infinite;
}

.drawer-aurora-violet {
  right: -150px;
  bottom: 10%;
  background: rgba(124, 58, 237, .2);
  animation: detailAuroraFloat 11s ease-in-out infinite reverse;
}

/* 漂浮粒子 */
.drawer-particles {
  position: absolute;
  z-index: -1;
  inset: 0;
  pointer-events: none;
}

.drawer-particles i {
  position: absolute;
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #67e8f9;
  box-shadow: 0 0 10px rgba(103, 232, 249, .8);
  animation: detailParticleFloat 6s ease-in-out infinite;
}

.drawer-particles i:nth-child(1) {
  top: 18%;
  left: 8%;
}

.drawer-particles i:nth-child(2) {
  top: 38%;
  right: 8%;
  background: #c4b5fd;
  animation-delay: 1.5s;
}

.drawer-particles i:nth-child(3) {
  top: 67%;
  left: 15%;
  background: #ffe08a;
  animation-delay: 2.8s;
}

.drawer-particles i:nth-child(4) {
  right: 13%;
  bottom: 8%;
  animation-delay: 4s;
}

/* 顶部改成更像交易终端抽屉 */
.detail-header {
  overflow: hidden;
  min-height: 76px;
  background:
    radial-gradient(circle at 90% 0%, rgba(124, 58, 237, .13), transparent 38%),
    linear-gradient(180deg, rgba(7, 12, 25, .96), rgba(5, 9, 18, .91));
  border-bottom: 1px solid rgba(103, 232, 249, .11);
  box-shadow:
    inset 0 -1px 0 rgba(167, 139, 250, .055),
    0 12px 34px rgba(0, 0, 0, .22);
}

.detail-header::after {
  position: absolute;
  left: 12%;
  right: 12%;
  bottom: 0;
  height: 1px;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    #22d3ee,
    #8b5cf6,
    transparent
  );
  background-size: 180% 100%;
  opacity: .55;
  animation: detailHeaderLine 5s linear infinite;
}

.back-btn,
.close-btn {
  position: relative;
  overflow: hidden;
  border-color: rgba(103, 232, 249, .13);
  background:
    linear-gradient(145deg, rgba(24, 34, 58, .88), rgba(7, 13, 25, .94));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .06),
    0 8px 22px rgba(0, 0, 0, .24);
  transition: transform .2s ease, border-color .2s ease, color .2s ease;
}

.back-btn:hover,
.close-btn:hover {
  color: #a5f3fc;
  border-color: rgba(103, 232, 249, .32);
  transform: translateY(-1px);
}

.header-eyebrow {
  color: #67e8f9;
  text-shadow: 0 0 12px rgba(103, 232, 249, .2);
}

.detail-header h2 {
  font-size: 19px;
  letter-spacing: -.2px;
}

/* 主视觉强化 */
.order-hero {
  isolation: isolate;
  border-color: rgba(103, 232, 249, .18);
  background:
    radial-gradient(circle at 88% 4%, rgba(124, 58, 237, .28), transparent 29%),
    radial-gradient(circle at 0% 100%, rgba(6, 182, 212, .2), transparent 35%),
    linear-gradient(145deg, rgba(13, 22, 43, .98), rgba(5, 11, 23, .99));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .075),
    0 24px 62px rgba(0, 0, 0, .42),
    0 0 30px rgba(34, 211, 238, .035);
}

.order-hero::before {
  position: absolute;
  z-index: 1;
  top: 0;
  left: -80px;
  width: 80px;
  height: 100%;
  content: "";
  transform: skewX(-22deg);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .075),
    transparent
  );
  animation: detailHeroShine 7s ease-in-out infinite;
  pointer-events: none;
}

.order-hero::after {
  position: absolute;
  z-index: 1;
  left: 14%;
  right: 14%;
  top: 0;
  height: 1px;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    #67e8f9,
    #a78bfa,
    transparent
  );
  opacity: .62;
}

.order-hero.trial {
  border-color: rgba(96, 165, 250, .24);
  background:
    radial-gradient(circle at 88% 4%, rgba(37, 99, 235, .3), transparent 29%),
    radial-gradient(circle at 0% 100%, rgba(124, 58, 237, .19), transparent 35%),
    linear-gradient(145deg, rgba(13, 23, 48, .98), rgba(5, 11, 24, .99));
}

.hero-grid {
  background-size: 26px 26px;
  animation: detailHeroGrid 18s linear infinite;
}

.hero-glow {
  animation: detailGlowPulse 6s ease-in-out infinite;
}

.type-badge,
.rule-chip,
.status-badge {
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.status-badge.running i,
.status-badge.redeeming i,
.status-badge.matured i {
  animation: detailStatusPulse 1.7s ease-in-out infinite;
}

.hero-main h3 {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  letter-spacing: -.35px;
}

.hero-assets > div {
  position: relative;
  overflow: hidden;
  background:
    linear-gradient(145deg, rgba(255, 255, 255, .07), rgba(255, 255, 255, .025));
  border-color: rgba(103, 232, 249, .09);
  transition: transform .22s ease, border-color .22s ease, background .22s ease;
}

.hero-assets > div::after {
  position: absolute;
  left: 14%;
  right: 14%;
  top: 0;
  height: 1px;
  content: "";
  background: linear-gradient(90deg, transparent, rgba(103, 232, 249, .42), transparent);
}

.hero-assets > div:hover {
  transform: translateY(-2px);
  border-color: rgba(103, 232, 249, .2);
  background:
    linear-gradient(145deg, rgba(34, 211, 238, .075), rgba(255, 255, 255, .026));
}

/* 内容卡片：更清晰、更专业 */
.detail-section {
  position: relative;
  overflow: hidden;
  border-color: rgba(129, 140, 248, .12);
  background:
    radial-gradient(circle at 100% 0%, rgba(99, 102, 241, .065), transparent 30%),
    linear-gradient(180deg, rgba(14, 23, 43, .9), rgba(6, 12, 24, .96));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, .05),
    0 18px 44px rgba(0, 0, 0, .24);
  transition: transform .22s ease, border-color .22s ease;
}

.detail-section::before {
  position: absolute;
  top: 0;
  left: 12%;
  right: 12%;
  height: 1px;
  content: "";
  background: linear-gradient(
    90deg,
    transparent,
    rgba(103, 232, 249, .28),
    rgba(167, 139, 250, .2),
    transparent
  );
}

.detail-section:hover {
  transform: translateY(-2px);
  border-color: rgba(103, 232, 249, .18);
}

.section-title span {
  color: #7183a5;
}

.section-title > b {
  color: #a5f3fc;
  background: rgba(6, 182, 212, .08);
  border: 1px solid rgba(34, 211, 238, .11);
}

.capital-track,
.progress-track {
  position: relative;
}

.capital-track::after,
.progress-track::after {
  position: absolute;
  inset: 0;
  content: "";
  background: linear-gradient(
    110deg,
    transparent 0%,
    rgba(255, 255, 255, .14) 48%,
    transparent 56%
  );
  background-size: 42px 100%;
  animation: detailProgressFlow 2.6s linear infinite;
  pointer-events: none;
}

.capital-legend > div,
.principal-card,
.trial-summary-grid > div,
.data-list > div,
.chain-list > div,
.rule-list > div,
.eligibility-list > div {
  transition: background .2s ease, border-color .2s ease, transform .2s ease;
}

.capital-legend > div:hover,
.principal-card:hover,
.trial-summary-grid > div:hover,
.rule-list > div:hover,
.eligibility-list > div:hover {
  transform: translateY(-1px);
  background: rgba(103, 232, 249, .045);
  border-color: rgba(103, 232, 249, .11);
}

.principal-card.earning {
  background:
    linear-gradient(145deg, rgba(6, 182, 212, .12), rgba(37, 99, 235, .075));
  border-color: rgba(103, 232, 249, .18);
}

.principal-card.earning strong {
  color: #67e8f9;
}

.data-list > div:nth-child(even),
.chain-list > div:nth-child(even) {
  background: rgba(99, 102, 241, .025);
}

.data-list > div:hover,
.chain-list > div:hover {
  background: rgba(6, 182, 212, .045);
}

.rule-list i {
  color: #a5f3fc;
  background: rgba(6, 182, 212, .09);
  border: 1px solid rgba(34, 211, 238, .11);
}

/* 体验策略用紫蓝差异化 */
.trial-section {
  background:
    radial-gradient(circle at 100% 0%, rgba(37, 99, 235, .11), transparent 30%),
    linear-gradient(180deg, rgba(12, 24, 51, .9), rgba(6, 12, 26, .97));
  border-color: rgba(96, 165, 250, .16);
}

.maturity-card.ready {
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, .04);
}

/* 链上凭证使用终端字体 */
.chain-list strong {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
}

/* 固定底部操作栏 */
.detail-footer {
  border-top: 1px solid rgba(103, 232, 249, .08);
  background:
    linear-gradient(
      180deg,
      rgba(5, 9, 18, .38),
      rgba(5, 9, 18, .97) 28%
    );
  box-shadow: 0 -18px 42px rgba(0, 0, 0, .28);
}

.detail-footer button {
  position: relative;
  overflow: hidden;
  transition: transform .2s ease, border-color .2s ease, box-shadow .2s ease;
}

.detail-footer button:not(:disabled):hover {
  transform: translateY(-2px);
}

.secondary-btn {
  border-color: rgba(103, 232, 249, .12);
  background:
    linear-gradient(145deg, rgba(20, 31, 52, .82), rgba(8, 14, 27, .92));
}

.primary-action {
  background:
    linear-gradient(135deg, #67e8f9 0%, #60a5fa 48%, #a78bfa 100%);
  box-shadow:
    0 14px 32px rgba(59, 130, 246, .24),
    0 0 24px rgba(103, 232, 249, .08);
}

.primary-action::before {
  position: absolute;
  top: -20px;
  bottom: -20px;
  left: -46px;
  width: 28px;
  content: "";
  transform: skewX(-22deg);
  background: linear-gradient(
    90deg,
    transparent,
    rgba(255, 255, 255, .45),
    transparent
  );
  animation: detailButtonShine 4.6s ease-in-out infinite;
}

.primary-action.trial {
  background:
    linear-gradient(135deg, #2563eb, #6366f1 54%, #8b5cf6);
}

/* 自定义滚动条 */
.detail-body {
  scrollbar-width: thin;
  scrollbar-color: rgba(103, 232, 249, .28) rgba(255, 255, 255, .03);
}

.detail-body::-webkit-scrollbar {
  width: 5px;
}

.detail-body::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, .025);
}

.detail-body::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: linear-gradient(180deg, rgba(34, 211, 238, .52), rgba(139, 92, 246, .5));
}

/* 动画 */
@keyframes detailGridMove {
  from { background-position: 0 0; }
  to { background-position: 56px 56px; }
}

@keyframes detailBeamSweep {
  0%, 18% {
    left: -160px;
    opacity: 0;
  }
  30% { opacity: .7; }
  68% {
    left: calc(100% + 150px);
    opacity: .24;
  }
  100% {
    left: calc(100% + 150px);
    opacity: 0;
  }
}

@keyframes detailAuroraFloat {
  0%, 100% {
    opacity: .48;
    transform: translate3d(0, 0, 0) scale(.9);
  }
  50% {
    opacity: .9;
    transform: translate3d(18px, -16px, 0) scale(1.08);
  }
}

@keyframes detailParticleFloat {
  0%, 100% {
    opacity: .2;
    transform: translateY(9px) scale(.75);
  }
  50% {
    opacity: 1;
    transform: translateY(-12px) scale(1.18);
  }
}

@keyframes detailHeaderLine {
  from { background-position: 0% 50%; }
  to { background-position: 180% 50%; }
}

@keyframes detailHeroShine {
  0%, 18% {
    left: -80px;
    opacity: 0;
  }
  30% { opacity: 1; }
  52% {
    left: calc(100% + 80px);
    opacity: 0;
  }
  100% {
    left: calc(100% + 80px);
    opacity: 0;
  }
}

@keyframes detailHeroGrid {
  from { background-position: 0 0; }
  to { background-position: 52px 52px; }
}

@keyframes detailGlowPulse {
  0%, 100% {
    opacity: .55;
    transform: scale(.88);
  }
  50% {
    opacity: 1;
    transform: scale(1.12);
  }
}

@keyframes detailStatusPulse {
  0%, 100% {
    opacity: .5;
    transform: scale(.82);
  }
  50% {
    opacity: 1;
    transform: scale(1.18);
  }
}

@keyframes detailProgressFlow {
  from { background-position: -42px 0; }
  to { background-position: 84px 0; }
}

@keyframes detailButtonShine {
  0%, 20% {
    left: -46px;
    opacity: 0;
  }
  32% { opacity: 1; }
  54% {
    left: calc(100% + 46px);
    opacity: 0;
  }
  100% {
    left: calc(100% + 46px);
    opacity: 0;
  }
}

@media (max-width: 380px) {
  .detail-header {
    min-height: 70px;
  }

  .back-btn,
  .close-btn {
    width: 38px;
    height: 38px;
    border-radius: 13px;
  }

  .detail-body {
    padding-right: 9px;
    padding-left: 9px;
  }

  .order-hero {
    padding-right: 13px;
    padding-left: 13px;
  }

  .hero-main h3 {
    font-size: 17px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .drawer-grid-bg,
  .drawer-scan-beam,
  .drawer-aurora,
  .drawer-particles i,
  .detail-header::after,
  .order-hero::before,
  .hero-grid,
  .hero-glow,
  .status-badge i,
  .capital-track::after,
  .progress-track::after,
  .primary-action::before {
    animation: none !important;
  }
}

</style>
